import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Form from "../components/Form";
import Button from "../components/Button";
import { getApiBaseUrl } from "../utils/apiConfig";
import "./AdminAuth.css";

export default function AdminRegisterOtpPage() {
  const nav = useNavigate();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef([]);

  const regId = typeof window !== "undefined" ? localStorage.getItem("adminRegId") : null;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    const newOtp = [...otp];
    newOtp[idx] = val.slice(-1);
    setOtp(newOtp);
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otp[idx]) {
        const newOtp = [...otp];
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");
    if (paste.length === 6) {
      setOtp(paste.split(""));
      setTimeout(() => inputsRef.current[5]?.focus(), 0);
      e.preventDefault();
    }
  };

  const handleResendOtp = async () => {
    if (!regId) {
      Swal.fire({
        title: "Session Expired",
        text: "Please register again to receive a new OTP.",
        icon: "warning",
        confirmButtonText: "Register",
      }).then(() => nav("/adminregister"));
      return;
    }

    if (cooldown > 0 || resending) return;

    try {
      setResending(true);
      setErr("");
      const API = getApiBaseUrl();
      const res = await fetch(`${API}/admin/register-resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ regId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to resend OTP.");

      setCooldown(60);
      Swal.fire({
        title: "OTP Resent",
        text: "A new 6-digit OTP has been sent to the company email (soloway.lk@gmail.com).",
        icon: "success",
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({
        title: "Error",
        text: e.message || "Failed to resend OTP.",
        icon: "error",
        confirmButtonText: "Close",
      });
    } finally {
      setResending(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!regId) {
      setErr("Registration session not found. Please register again.");
      return;
    }

    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setErr("Please enter the 6-digit OTP.");
      return;
    }

    try {
      setBusy(true);
      const API = getApiBaseUrl();
      const res = await fetch(`${API}/admin/register-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ regId, otp: otpValue }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to verify OTP.");

      await Swal.fire({
        title: "Success",
        text: "Admin registered successfully!",
        icon: "success",
        confirmButtonText: "Go to Login",
        customClass: {
          confirmButton: 'custom-swal-confirm'
        },
        buttonsStyling: false,
      });

      localStorage.removeItem("adminRegId");
      nav("/adminlogin");
    } catch (e) {
      Swal.fire({
        title: "Error",
        text: e.message || "Failed to verify OTP.",
        icon: "error",
        confirmButtonText: "Try Again",
        customClass: {
          confirmButton: 'custom-swal-cancel'
        },
        buttonsStyling: false,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-container">
      <Form
        title="Admin OTP Verification"
        subtitle="Enter the 6-digit OTP sent to the company email (soloway.lk@gmail.com)"
        onSubmit={onSubmit}
      >
        <div className="otp-inputs">
          {otp.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (inputsRef.current[idx] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e, idx)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              onPaste={idx === 0 ? handlePaste : undefined}
              autoFocus={idx === 0}
              className="otp-box"
            />
          ))}
        </div>
        {err && <div className="error-text">{err}</div>}
        <Button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Verifying..." : "Verify OTP"}
        </Button>

        <div className="resend-container" style={{ margin: "14px 0 0", textAlign: "center", fontSize: "14px", color: "#64748b" }}>
          <span>Didn't receive the OTP?</span>
          <button
            type="button"
            className="link-btn"
            onClick={handleResendOtp}
            disabled={cooldown > 0 || resending}
            style={{ fontWeight: 600 }}
          >
            {resending ? "Sending..." : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
          </button>
        </div>

        <div className="link-container">
          <span>Already have an account?</span>
          <button type="button" className="link-btn" onClick={() => nav("/adminlogin")}>
            Back to Login
          </button>
        </div>
      </Form>
    </div>
  );
}

